import { propState, type CellChange, type Command, type EntityState, type LayerTerrain, type PropState, type TileCellChange } from "../document/commands.ts";
import { EMPTY_TILE, packedToTileRef, tileRefToPacked } from "../document/packed-tile.ts";
import { sameJson } from "../document/same-json.ts";
import { COLLISION_CELLS_PER_TILE, type EntityInstance, type InstanceFootprint, type JsonValue, type LayerGroup, type MapDocument, type PropBehaviour, type PropInstance, type TileRef, type TilesetRef } from "../document/types.ts";
import { sortedRecord } from "../document/validate.ts";
import { faceRows } from "../terrain/faces.ts";
import type { TerrainDefinition, TerrainFace } from "../terrain/types.ts";
import { carriedFaceHeight, drawableFaceHeight, faceHeightRange } from "./face-height.ts";
import { fieldsFromSchema, type EntityTypeDefinition } from "./schema.ts";

/**
 * Pure builders for the commands the panels issue (ADR-021 item 2). Each reads the current map, checks its
 * preconditions and returns the command; the store executes it.
 */

/**
 * A layer by id.
 *
 * @param map The map.
 * @param layerId Layer id.
 * @returns The layer.
 * @throws Error when missing.
 */
export function requireLayer(map: MapDocument, layerId: string): MapDocument["layers"][number] {
  const layer = map.layers.find((l) => l.id === layerId);
  if (layer === undefined) {
    throw new Error(`layer ${layerId} does not exist`);
  }
  return layer;
}

/**
 * An entity by layer and id.
 *
 * @param map The map.
 * @param layerId Entity layer id.
 * @param entityId Entity id.
 * @returns The entity.
 * @throws Error when missing.
 */
export function requireEntity(map: MapDocument, layerId: string, entityId: string): EntityInstance {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "entity") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not entity`);
  }
  const entity = layer.entities.find((e) => e.id === entityId);
  if (entity === undefined) {
    throw new Error(`entity ${entityId} does not exist in layer ${layerId}`);
  }
  return entity;
}

/**
 * A prop by layer and id.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @returns The prop and its index.
 * @throws Error when missing.
 */
export function requireProp(map: MapDocument, layerId: string, propId: string): { prop: PropInstance; index: number } {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "prop") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not prop`);
  }
  const index = layer.props.findIndex((p) => p.id === propId);
  const prop = layer.props[index];
  if (prop === undefined) {
    throw new Error(`prop ${propId} does not exist in layer ${layerId}`);
  }
  return { prop, index };
}

/**
 * Builds a rename, or null when neither the name nor the flag changes. Given `autoName`, a terrain layer's
 * flag is set in the same command (ADR-038 amendment of 2026-09-26, decision 7).
 *
 * @param map The map.
 * @param layerId Layer id.
 * @param name New name.
 * @param autoName The terrain layer's flag after the rename; omitted to leave the flag as it is.
 * @returns The command.
 * @throws Error when a flag is given for a layer that is not a terrain layer.
 */
export function renameLayerCommand(map: MapDocument, layerId: string, name: string, autoName?: boolean): Command | null {
  const layer = requireLayer(map, layerId);
  if (autoName !== undefined && layer.kind !== "terrain") {
    throw new Error(`layer ${layerId} is ${layer.kind}; only a terrain layer follows its terrain's name`);
  }
  const flag = autoName === undefined || layer.kind !== "terrain" || layer.autoName === autoName ? undefined : { ...(layer.autoName === undefined ? {} : { before: layer.autoName }), after: autoName };
  if (layer.name === name && flag === undefined) {
    return null;
  }
  return { kind: "renameLayer", mapId: map.id, layerId, before: layer.name, after: name, ...(flag === undefined ? {} : { autoName: flag }) };
}

/**
 * Builds a change of a map's properties (P1.6-T28), or null when the record equals the map's, keys in any
 * order.
 *
 * @param map The map.
 * @param properties The whole new record.
 * @returns The command.
 * @throws Error when a key is `__proto__`.
 */
export function setMapPropertiesCommand(map: MapDocument, properties: { readonly [key: string]: JsonValue }): Command | null {
  const after = sortedRecord(properties, `maps.${map.id}.properties`);
  return JSON.stringify(sortedRecord(map.properties, `maps.${map.id}.properties`)) === JSON.stringify(after) ? null : { kind: "setMapProperties", mapId: map.id, before: map.properties, after };
}

/**
 * Builds a terrain change after checking every painted corner is a colour the new terrain defines. The
 * layer's face height travels in the same command (ADR-072 decision 6): kept when the new terrain's face can
 * draw it, dropped when that face is taller or the terrain declares none, and dropped as well when it equals
 * the new face's own rows.
 *
 * @param map The map.
 * @param layerId Terrain layer id.
 * @param terrain New terrain, its `face` the one the editor can use.
 * @param variant New variant.
 * @param name New layer name, when the retarget renames the layer in the same command.
 * @returns The command.
 * @throws Error when the layer is not a terrain layer or a corner uses a colour the terrain lacks.
 */
export function setLayerTerrainCommand(map: MapDocument, layerId: string, terrain: TerrainDefinition, variant: string, name?: string): Command {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "terrain") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not terrain`);
  }
  const colours = new Set(terrain.colors.map((c) => c.index));
  for (let i = 0; i < layer.corners.length; i += 1) {
    const colour = layer.corners[i] as number;
    if (colour !== 0 && !colours.has(colour)) {
      throw new Error(`vertex ${i} uses colour ${colour}, which terrain ${terrain.id} does not define`);
    }
  }
  const renamed = name !== undefined && name !== layer.name;
  const carried = carriedFaceHeight(layer.faceHeight, terrain.face);
  const before: LayerTerrain = { terrain: layer.terrain, variant: layer.variant, ...(renamed ? { name: layer.name } : {}), ...(layer.faceHeight === undefined ? {} : { faceHeight: layer.faceHeight }) };
  const after: LayerTerrain = { terrain: terrain.id, variant, ...(renamed ? { name } : {}), ...(carried === undefined ? {} : { faceHeight: carried }) };
  return { kind: "setLayerTerrain", mapId: map.id, layerId, before, after };
}

/**
 * Builds a face height change (ADR-072 decision 6) after checking the layer's terrain can draw the height.
 * The face's own rows are written as absence, so a layer set back reads, saves and compiles as one never
 * touched.
 *
 * @param map The map.
 * @param layerId Terrain layer id.
 * @param face The layer's terrain's face as the editor uses it, or undefined when it declares none.
 * @param height The height typed.
 * @returns The command, or null when the layer already holds that height.
 * @throws Error when the layer is not a terrain layer, its terrain declares no face, or the height is not an
 *   integer from the face's rows to `FACE_HEIGHT_MAX`.
 */
export function setLayerFaceHeightCommand(map: MapDocument, layerId: string, face: TerrainFace | undefined, height: number): Command | null {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "terrain") {
    throw new Error(`layer ${layerId} is not a terrain layer, so it takes no cliff height`);
  }
  if (face === undefined) {
    throw new Error(`terrain ${layer.terrain} declares no face, so layer ${layerId} takes no cliff height`);
  }
  if (!drawableFaceHeight(face, height)) {
    const { min, max } = faceHeightRange(face);
    throw new Error(`cliff height ${height} is not a whole number from ${min} to ${max}`);
  }
  const after = height === faceRows(face) ? undefined : height;
  if (after === layer.faceHeight) {
    return null;
  }
  return { kind: "setLayerFaceHeight", mapId: map.id, layerId, ...(layer.faceHeight === undefined ? {} : { before: layer.faceHeight }), ...(after === undefined ? {} : { after }) };
}

/**
 * Builds an entity addition with the type's schema defaults.
 *
 * @param map The map.
 * @param layerId Entity layer id.
 * @param definition Entity type.
 * @param id New entity id.
 * @param x Position x.
 * @param y Position y.
 * @returns The command.
 * @throws Error when the layer is not an entity layer.
 */
export function addEntityCommand(map: MapDocument, layerId: string, definition: EntityTypeDefinition, id: string, x: number, y: number): Command {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "entity") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not entity`);
  }
  const fields: Record<string, JsonValue> = {};
  for (const field of fieldsFromSchema(definition.schema)) {
    if (field.defaultValue !== null) {
      fields[field.name] = field.defaultValue;
    }
  }
  return { kind: "addEntity", mapId: map.id, layerId, index: layer.entities.length, entity: { id, type: definition.type, x, y, fields } };
}

/**
 * Builds an entity update.
 *
 * @param map The map.
 * @param layerId Entity layer id.
 * @param entityId Entity id.
 * @param after New position and fields.
 * @returns The command.
 */
export function updateEntityCommand(map: MapDocument, layerId: string, entityId: string, after: EntityState): Command {
  const entity = requireEntity(map, layerId, entityId);
  return { kind: "updateEntity", mapId: map.id, layerId, entityId, before: { x: entity.x, y: entity.y, fields: entity.fields }, after };
}

/**
 * Builds an entity removal.
 *
 * @param map The map.
 * @param layerId Entity layer id.
 * @param entityId Entity id.
 * @returns The command.
 */
export function removeEntityCommand(map: MapDocument, layerId: string, entityId: string): Command {
  const layer = requireLayer(map, layerId);
  const entity = requireEntity(map, layerId, entityId);
  return { kind: "removeEntity", mapId: map.id, layerId, index: layer.kind === "entity" ? layer.entities.indexOf(entity) : 0, entity };
}

/**
 * Builds a prop placement.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param prop The prop.
 * @returns The command.
 * @throws Error when the layer is not a prop layer.
 */
export function addPropCommand(map: MapDocument, layerId: string, prop: PropInstance): Command {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "prop") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not prop`);
  }
  return { kind: "addProp", mapId: map.id, layerId, index: layer.props.length, prop };
}

/**
 * Builds a prop move.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @param x New cell x.
 * @param y New cell y.
 * @returns The command.
 */
export function movePropCommand(map: MapDocument, layerId: string, propId: string, x: number, y: number): Command {
  const { prop } = requireProp(map, layerId, propId);
  return { kind: "updateProp", mapId: map.id, layerId, propId, before: propState(prop), after: { ...propState(prop), x, y } };
}

/**
 * Builds the command that keeps a prop as placed, or stops keeping it (ADR-040 amendment of 2026-10-04,
 * P1.6-T88): the mark alone changes, the prop stays where it stands.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @param keep True to keep the prop as placed, false to stop.
 * @returns The command, or null when the layer holds no such prop or the prop is already so.
 */
export function keepPropPlacementCommand(map: MapDocument, layerId: string, propId: string, keep: boolean): Command | null {
  const layer = map.layers.find((candidate) => candidate.id === layerId);
  const prop = layer?.kind === "prop" ? layer.props.find((candidate) => candidate.id === propId) : undefined;
  if (prop === undefined || (prop.keepPlacement === true) === keep) {
    return null;
  }
  const { keepPlacement: _kept, ...rest } = propState(prop);
  return { kind: "updateProp", mapId: map.id, layerId, propId, before: propState(prop), after: keep ? { ...rest, keepPlacement: true } : rest };
}

/**
 * Builds a prop update with any subset of its state replaced. A change of frame that names no footprint, and
 * any change that leaves the prop with a behaviour, drops the prop's own footprint, whose cells belong to the
 * frame it had and to a plain prop (ADR-040 amendment of 2026-10-04); `footprint: undefined` clears it.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @param changes Fields to change.
 * @returns The command.
 */
export function changePropCommand(map: MapDocument, layerId: string, propId: string, changes: Partial<Omit<PropState, "footprint">> & { readonly footprint?: InstanceFootprint | undefined }): Command {
  const { prop } = requireProp(map, layerId, propId);
  const before = propState(prop);
  const { footprint, ...after } = { ...before, ...changes };
  const reframed = !sameJson(before.frame, after.frame) && !Object.hasOwn(changes, "footprint");
  return { kind: "updateProp", mapId: map.id, layerId, propId, before, after: footprint === undefined || reframed || after.behaviour !== undefined ? after : { ...after, footprint } };
}

/**
 * Builds a prop's behaviour change (ADR-053): the whole behaviour is replaced, or removed when none is
 * given, as one undoable command. The `behaviour` key is absent rather than undefined on a prop that carries
 * none, so the recorded `before` of a later command matches a prop read from a save. A prop given a behaviour
 * loses its own footprint in the same command, since a thing blocks by its states (ADR-040 amendment of
 * 2026-10-04).
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @param behaviour The behaviour to write, or undefined to switch it off.
 * @returns The command.
 * @throws Error when the prop does not exist.
 */
export function setPropBehaviourCommand(map: MapDocument, layerId: string, propId: string, behaviour: PropBehaviour | undefined): Command {
  const { prop } = requireProp(map, layerId, propId);
  const before = propState(prop);
  const { behaviour: _dropped, ...rest } = before;
  const { footprint: _own, ...plain } = rest;
  return { kind: "updateProp", mapId: map.id, layerId, propId, before, after: behaviour === undefined ? rest : { ...plain, behaviour } };
}

/**
 * Builds the Behaviours view's Clear (P1.6-T30): every prop of the map carrying a behaviour of the type loses
 * it, as one command (a compound of one behaviour change per prop, or the change alone for one prop), so one
 * undo brings every one back as it was.
 *
 * @param map The map.
 * @param type The behaviour type's id.
 * @returns The command, or null when no prop carries the type.
 */
export function clearBehaviourTypeCommand(map: MapDocument, type: string): Command | null {
  const commands: Command[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type === type) {
        commands.push(setPropBehaviourCommand(map, layer.id, prop.id, undefined));
      }
    }
  }
  if (commands.length === 0) {
    return null;
  }
  return commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands };
}

/**
 * Builds a prop removal.
 *
 * @param map The map.
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @returns The command.
 */
export function removePropCommand(map: MapDocument, layerId: string, propId: string): Command {
  const { prop, index } = requireProp(map, layerId, propId);
  return { kind: "removeProp", mapId: map.id, layerId, index, prop };
}

const DEFAULT_VARIANT = "default";

/**
 * Kinds of layer the editor can create.
 */
export type NewLayerKind = "terrain" | "prop" | "entity" | "collision" | "tile" | "zone" | "door";

/**
 * Builds an empty layer of a kind for a map. A terrain layer takes the given terrain, the `default` variant
 * and an all-zero corner grid; a collision layer an all-zero byte per cell; a tile layer every cell empty; a
 * zone layer no types and every cell unpainted; a door layer no doors and every cell unpainted.
 *
 * @param map The map, for its size.
 * @param kind Layer kind.
 * @param id New layer id.
 * @param name Display name.
 * @param terrain Terrain for a terrain layer; ignored otherwise.
 * @returns The layer.
 * @throws Error when a terrain layer is asked for without a terrain.
 */
export function newLayer(map: MapDocument, kind: NewLayerKind, id: string, name: string, terrain: TerrainDefinition | undefined): MapDocument["layers"][number] {
  switch (kind) {
    case "terrain":
      if (terrain === undefined) {
        throw new Error("a terrain layer needs a terrain; the context defines none this map's tilesets can use");
      }
      return { kind, id, name, terrain: terrain.id, variant: DEFAULT_VARIANT, corners: new Uint8Array((map.width + 1) * (map.height + 1)) };
    case "prop":
      return { kind, id, name, props: [] };
    case "entity":
      return { kind, id, name, entities: [] };
    case "collision":
      return { kind, id, name, cells: new Uint8Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE) };
    case "tile":
      return { kind, id, name, tiles: new Uint32Array(map.width * map.height).fill(EMPTY_TILE) };
    case "zone":
      return { kind, id, name, types: [], cells: new Uint8Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE) };
    case "door":
      return { kind, id, name, doors: [], cells: new Uint8Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE) };
  }
}

/**
 * Builds a tile paint (ADR-033): every listed cell takes the tile, recorded against its current tile so
 * the command inverts exactly. Cells already holding it and duplicate indices are left out.
 *
 * @param map The map.
 * @param layerId Tile layer id.
 * @param indices Cell indices.
 * @param ref Tile to place, or null to erase.
 * @param tilesets The world's tilesets, for the packed form.
 * @returns The command, or null when nothing would change.
 * @throws Error when the layer is missing or not a tile layer, an index is outside the map, or the tile's
 *   tileset is not in the world.
 */
export function paintTilesCommand(map: MapDocument, layerId: string, indices: readonly number[], ref: TileRef | null, tilesets: readonly TilesetRef[]): Command | null {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "tile") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not tile`);
  }
  const packed = tileRefToPacked(ref, tilesets);
  const cells: TileCellChange[] = [];
  const seen = new Set<number>();
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= layer.tiles.length) {
      throw new Error(`cell index ${index} is outside the ${map.width}x${map.height} map`);
    }
    const current = layer.tiles[index] as number;
    if (current !== packed && !seen.has(index)) {
      seen.add(index);
      cells.push({ index, before: packedToTileRef(current, tilesets), after: ref });
    }
  }
  return cells.length === 0 ? null : { kind: "setTiles", mapId: map.id, layerId, cells };
}

/**
 * Builds a collision paint: every listed cell takes the value, recorded against its current byte so the
 * command inverts exactly. Cells already at the value are left out.
 *
 * @param map The map.
 * @param layerId Collision layer id.
 * @param indices Cell indices.
 * @param value Byte to write.
 * @returns The command, or null when nothing would change.
 * @throws Error when the layer is missing or not a collision layer, or an index is outside the map.
 */
export function paintCollisionCommand(map: MapDocument, layerId: string, indices: readonly number[], value: number): Command | null {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "collision") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not collision`);
  }
  const cells: CellChange[] = [];
  const seen = new Set<number>();
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= layer.cells.length) {
      throw new Error(`cell index ${index} is outside the ${map.width}x${map.height} map`);
    }
    const before = layer.cells[index] as number;
    if (before !== value && !seen.has(index)) {
      seen.add(index);
      cells.push({ index, before, after: value });
    }
  }
  return cells.length === 0 ? null : { kind: "paintCollision", mapId: map.id, layerId, cells };
}

/**
 * Builds a layer insertion, on top of the map unless an index is given (a layer joining a group lands in the
 * group's block, right above its top member). An insertion under the Players band adds a ground layer.
 *
 * @param map The map.
 * @param layer The layer to add.
 * @param group A group to create in the same command, which the layer joins.
 * @param index Where the layer goes, bottom first; the top of the map by default.
 * @returns The command.
 */
export function addLayerCommand(map: MapDocument, layer: MapDocument["layers"][number], group?: LayerGroup, index: number = map.layers.length): Command {
  return group === undefined ? { kind: "addLayer", mapId: map.id, index, layer } : { kind: "addLayer", mapId: map.id, index, layer, group };
}

/**
 * Builds a layer removal.
 *
 * @param map The map.
 * @param layerId Layer id.
 * @returns The command.
 * @throws Error when the layer is missing.
 */
export function removeLayerCommand(map: MapDocument, layerId: string): Command {
  const index = map.layers.findIndex((l) => l.id === layerId);
  if (index < 0) {
    throw new Error(`map ${map.id} has no layer ${layerId}`);
  }
  return { kind: "removeLayer", mapId: map.id, index, layer: map.layers[index] as MapDocument["layers"][number] };
}

/**
 * Builds the removal of several layers as one command: the existing layer removals, the highest layer first,
 * so each removal's index still holds once the ones above it have run, and one undo restores them all in
 * place. A lone layer is its own removal.
 *
 * @param map The map.
 * @param layerIds The layers, in any order.
 * @returns The command.
 * @throws Error when no layer is named or one is missing.
 */
export function removeLayersCommand(map: MapDocument, layerIds: readonly string[]): Command {
  if (layerIds.length === 0) {
    throw new Error("no layer to remove");
  }
  const commands = [...new Set(layerIds)].map((layerId) => removeLayerCommand(map, layerId)).sort((a, b) => (a.kind === "removeLayer" && b.kind === "removeLayer" ? b.index - a.index : 0));
  return commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands };
}
